'use client'

import Link from 'next/link'
import { useMemo, useState } from 'react'

type CalculatorId =
  | 'painting-interior'
  | 'tile-full'
  | 'concrete-slab'
  | 'roofing-calculator'
  | 'true-hourly-rate'
  | 'job-profitability'

type DemoInput = {
  key: string
  label: string
  value: number
  min: number
  max: number
  step: number
  suffix: string
}

type DemoResult = {
  headline: string
  total: string
  metrics: Array<{ label: string; value: string }>
  assumptions: string[]
  handoff: string[]
}

type DemoConfig = {
  id: CalculatorId
  trade: string
  name: string
  description: string
  demoTitle: string
  demoSubtitle: string
  returns: string[]
  inputs: DemoInput[]
  icon: React.ReactNode
  calculate: (values: Record<string, number>) => DemoResult
}

function currency(value: number) {
  return `R${Math.round(value).toLocaleString('en-ZA')}`
}

function numberValue(value: number, digits = 1) {
  return value.toLocaleString('en-ZA', {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  })
}

function whole(value: number) {
  return Math.round(value).toLocaleString('en-ZA')
}

function getValues(inputs: DemoInput[]) {
  return Object.fromEntries(inputs.map((input) => [input.key, input.value]))
}

const calculatorIconStroke = '#c2571a'

const CALCULATORS: DemoConfig[] = [
  {
    id: 'painting-interior',
    trade: 'Painting',
    name: 'Interior Paint',
    description: 'A proper room-by-room paint estimate with wall area, ceilings, coats, waste, labour, and prep allowance.',
    demoTitle: '3 bedroom repaint in Durbanville',
    demoSubtitle: 'Two matching rooms at 3.2 x 3.6m, one larger room at 4.1 x 3.8m, all 2.7m high.',
    returns: ['Wall and ceiling sqm', 'Litres plus waste', 'Prep and labour allowance'],
    inputs: [
      { key: 'coats', label: 'Coats', value: 2, min: 1, max: 3, step: 1, suffix: '' },
      { key: 'coverage', label: 'Coverage', value: 12, min: 8, max: 16, step: 0.5, suffix: 'sqm/L' },
      { key: 'waste', label: 'Waste', value: 10, min: 0, max: 20, step: 1, suffix: '%' },
      { key: 'paintPrice', label: 'Paint price', value: 120, min: 70, max: 220, step: 5, suffix: 'R/L' },
      { key: 'labourPerSqm', label: 'Labour rate', value: 28, min: 12, max: 60, step: 1, suffix: 'R/sqm' },
    ],
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
        <path d="M4 16l3-3 9-9-3-3-9 9-3 3h3z" stroke={calculatorIconStroke} strokeWidth="1.4" strokeLinejoin="round"/>
        <path d="M14 4l2 2" stroke={calculatorIconStroke} strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M15 13c0 1.1.9 2 2 2s2-.9 2-2-2-3-2-3-2 1.9-2 3z" stroke={calculatorIconStroke} strokeWidth="1.3"/>
      </svg>
    ),
    calculate: (values) => {
      const rooms = [
        { label: 'Bedroom 1', length: 3.2, width: 3.6, height: 2.7 },
        { label: 'Bedroom 2', length: 3.2, width: 3.6, height: 2.7 },
        { label: 'Bedroom 3', length: 4.1, width: 3.8, height: 2.7 },
      ]
      const openingAllowance = 1.8
      const roomRows = rooms.map((room) => {
        const walls = (2 * (room.length + room.width) * room.height) - openingAllowance
        const ceiling = room.length * room.width
        return { ...room, walls, ceiling, total: walls + ceiling }
      })
      const paintableArea = roomRows.reduce((sum, room) => sum + room.total, 0)
      const litres = (paintableArea * values.coats / values.coverage) * (1 + values.waste / 100)
      const paintCost = litres * values.paintPrice
      const labourCost = paintableArea * values.labourPerSqm
      const prepAllowance = 650
      const total = paintCost + labourCost + prepAllowance

      return {
        headline: 'Room-by-room painting estimate',
        total: currency(total),
        metrics: [
          { label: 'Paintable area', value: `${numberValue(paintableArea)} sqm` },
          { label: 'Paint required', value: `${numberValue(litres)} L` },
          { label: 'Material cost', value: currency(paintCost) },
          { label: 'Labour + prep', value: currency(labourCost + prepAllowance) },
        ],
        assumptions: [
          `${whole(values.coats)} coats at ${numberValue(values.coverage)} sqm/L coverage`,
          `${whole(values.waste)}% waste and touch-up allowance`,
          `${currency(values.paintPrice)} per litre and ${currency(values.labourPerSqm)} per sqm labour`,
        ],
        handoff: roomRows.map((room) => `${room.label}: ${numberValue(room.total)} sqm walls + ceiling`),
      }
    },
  },
  {
    id: 'tile-full',
    trade: 'Tiling',
    name: 'Tiles + Adhesive + Grout',
    description: 'Turns multiple tiled areas into tile boxes, adhesive bags, grout, wastage, and a quote-ready material total.',
    demoTitle: 'Bathroom floor, shower wall, and splashback',
    demoSubtitle: 'Three tiled zones with different sizes, using 600 x 600mm porcelain tiles.',
    returns: ['Tiles with wastage', 'Adhesive and grout', 'Material cost'],
    inputs: [
      { key: 'tileSize', label: 'Tile size', value: 600, min: 300, max: 1200, step: 100, suffix: 'mm' },
      { key: 'waste', label: 'Waste', value: 12, min: 5, max: 25, step: 1, suffix: '%' },
      { key: 'tilePrice', label: 'Tile price', value: 189, min: 90, max: 450, step: 5, suffix: 'R/sqm' },
      { key: 'adhesivePrice', label: 'Adhesive', value: 145, min: 80, max: 260, step: 5, suffix: 'R/bag' },
      { key: 'labourPerSqm', label: 'Labour rate', value: 185, min: 90, max: 350, step: 5, suffix: 'R/sqm' },
    ],
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
        <rect x="2" y="2" width="7" height="7" rx="1.2" stroke={calculatorIconStroke} strokeWidth="1.4"/>
        <rect x="11" y="2" width="7" height="7" rx="1.2" stroke={calculatorIconStroke} strokeWidth="1.4"/>
        <rect x="2" y="11" width="7" height="7" rx="1.2" stroke={calculatorIconStroke} strokeWidth="1.4"/>
        <rect x="11" y="11" width="7" height="7" rx="1.2" stroke={calculatorIconStroke} strokeWidth="1.4"/>
      </svg>
    ),
    calculate: (values) => {
      const areas = [
        { label: 'Bathroom floor', sqm: 8.64 },
        { label: 'Shower feature wall', sqm: 5.4 },
        { label: 'Vanity splashback', sqm: 1.8 },
      ]
      const baseArea = areas.reduce((sum, area) => sum + area.sqm, 0)
      const areaWithWaste = baseArea * (1 + values.waste / 100)
      const tileArea = (values.tileSize / 1000) ** 2
      const tiles = Math.ceil(areaWithWaste / tileArea)
      const adhesiveBags = Math.ceil(areaWithWaste * 4.5 / 20)
      const groutKg = Math.ceil(areaWithWaste * 0.35)
      const tileCost = areaWithWaste * values.tilePrice
      const adhesiveCost = adhesiveBags * values.adhesivePrice
      const groutCost = groutKg * 48
      const labourCost = baseArea * values.labourPerSqm
      const total = tileCost + adhesiveCost + groutCost + labourCost

      return {
        headline: 'Multi-zone tiling estimate',
        total: currency(total),
        metrics: [
          { label: 'Measured area', value: `${numberValue(baseArea)} sqm` },
          { label: 'Tiles to order', value: `${whole(tiles)} tiles` },
          { label: 'Adhesive', value: `${whole(adhesiveBags)} bags` },
          { label: 'Grout', value: `${whole(groutKg)} kg` },
        ],
        assumptions: [
          `${whole(values.waste)}% wastage for cuts and breakages`,
          `${whole(values.tileSize)} x ${whole(values.tileSize)}mm tiles`,
          `Adhesive at 4.5kg/sqm and grout at 0.35kg/sqm`,
        ],
        handoff: areas.map((area) => `${area.label}: ${numberValue(area.sqm)} sqm`),
      }
    },
  },
  {
    id: 'concrete-slab',
    trade: 'Building',
    name: 'Concrete Slab',
    description: 'Combines slab zones, thickness, wastage, ready-mix volume, reinforcing allowance, and labour.',
    demoTitle: 'Driveway apron and small patio slab',
    demoSubtitle: 'Two separate pours with one thickness, plus a practical waste allowance.',
    returns: ['Concrete m3', 'Ready-mix cost', 'Labour and reinforcing'],
    inputs: [
      { key: 'thickness', label: 'Thickness', value: 100, min: 75, max: 150, step: 5, suffix: 'mm' },
      { key: 'waste', label: 'Waste', value: 7, min: 0, max: 15, step: 1, suffix: '%' },
      { key: 'readyMix', label: 'Ready mix', value: 1450, min: 950, max: 2200, step: 50, suffix: 'R/m3' },
      { key: 'meshPerSqm', label: 'Mesh allowance', value: 42, min: 20, max: 85, step: 1, suffix: 'R/sqm' },
      { key: 'labourPerSqm', label: 'Labour rate', value: 95, min: 45, max: 180, step: 5, suffix: 'R/sqm' },
    ],
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
        <rect x="2" y="8" width="16" height="10" rx="1.5" stroke={calculatorIconStroke} strokeWidth="1.4"/>
        <path d="M5 8V5a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v3" stroke={calculatorIconStroke} strokeWidth="1.4"/>
        <path d="M7 13h6" stroke={calculatorIconStroke} strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
    calculate: (values) => {
      const slabs = [
        { label: 'Driveway apron', length: 5.8, width: 3.2 },
        { label: 'Patio slab', length: 4.2, width: 2.6 },
      ]
      const area = slabs.reduce((sum, slab) => sum + slab.length * slab.width, 0)
      const volume = area * (values.thickness / 1000) * (1 + values.waste / 100)
      const concreteCost = volume * values.readyMix
      const meshCost = area * values.meshPerSqm
      const labourCost = area * values.labourPerSqm
      const total = concreteCost + meshCost + labourCost

      return {
        headline: 'Two-zone slab estimate',
        total: currency(total),
        metrics: [
          { label: 'Slab area', value: `${numberValue(area)} sqm` },
          { label: 'Concrete volume', value: `${numberValue(volume, 2)} m3` },
          { label: 'Concrete cost', value: currency(concreteCost) },
          { label: 'Mesh + labour', value: currency(meshCost + labourCost) },
        ],
        assumptions: [
          `${whole(values.thickness)}mm slab thickness`,
          `${whole(values.waste)}% overage for uneven sub-base and spillage`,
          `${currency(values.readyMix)} per m3 ready-mix`,
        ],
        handoff: slabs.map((slab) => `${slab.label}: ${numberValue(slab.length * slab.width)} sqm`),
      }
    },
  },
  {
    id: 'roofing-calculator',
    trade: 'Roofing',
    name: 'Roofing Estimator',
    description: 'Pitch-corrects roof area and converts it into sheet quantities, underlay, ridge caps, fixings, and labour.',
    demoTitle: 'Double-pitch roof over a garage and lean-to',
    demoSubtitle: 'Footprint area is adjusted by roof pitch before material quantities are calculated.',
    returns: ['Pitch-corrected area', 'Sheets and underlay', 'Ridge and fixing allowance'],
    inputs: [
      { key: 'pitch', label: 'Roof pitch', value: 26, min: 5, max: 45, step: 1, suffix: 'deg' },
      { key: 'waste', label: 'Waste', value: 8, min: 3, max: 18, step: 1, suffix: '%' },
      { key: 'sheetCoverage', label: 'Sheet cover', value: 1.9, min: 1.2, max: 2.6, step: 0.1, suffix: 'sqm' },
      { key: 'sheetPrice', label: 'Sheet price', value: 235, min: 120, max: 480, step: 5, suffix: 'R' },
      { key: 'labourPerSqm', label: 'Labour rate', value: 130, min: 70, max: 260, step: 5, suffix: 'R/sqm' },
    ],
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
        <path d="M2 9l8-7 8 7" stroke={calculatorIconStroke} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M4 9v8h12V9" stroke={calculatorIconStroke} strokeWidth="1.4" strokeLinejoin="round"/>
        <path d="M8 17v-5h4v5" stroke={calculatorIconStroke} strokeWidth="1.3"/>
      </svg>
    ),
    calculate: (values) => {
      const zones = [
        { label: 'Garage roof footprint', length: 6.4, width: 5.2 },
        { label: 'Lean-to footprint', length: 4.8, width: 2.4 },
      ]
      const footprint = zones.reduce((sum, zone) => sum + zone.length * zone.width, 0)
      const pitchArea = footprint / Math.cos(values.pitch * Math.PI / 180)
      const orderArea = pitchArea * (1 + values.waste / 100)
      const sheets = Math.ceil(orderArea / values.sheetCoverage)
      const underlayRolls = Math.ceil(orderArea / 30)
      const ridgeCaps = Math.ceil(6.4 / 1.8)
      const sheetCost = sheets * values.sheetPrice
      const accessoryCost = underlayRolls * 520 + ridgeCaps * 95 + sheets * 18
      const labourCost = pitchArea * values.labourPerSqm
      const total = sheetCost + accessoryCost + labourCost

      return {
        headline: 'Pitch-corrected roofing estimate',
        total: currency(total),
        metrics: [
          { label: 'Roof surface', value: `${numberValue(pitchArea)} sqm` },
          { label: 'Sheets to order', value: `${whole(sheets)}` },
          { label: 'Underlay rolls', value: `${whole(underlayRolls)}` },
          { label: 'Ridge caps', value: `${whole(ridgeCaps)}` },
        ],
        assumptions: [
          `${whole(values.pitch)} degree pitch correction applied`,
          `${whole(values.waste)}% material overage`,
          `${numberValue(values.sheetCoverage)} sqm cover per sheet`,
        ],
        handoff: zones.map((zone) => `${zone.label}: ${numberValue(zone.length * zone.width)} sqm footprint`),
      }
    },
  },
  {
    id: 'true-hourly-rate',
    trade: 'All trades',
    name: 'True Labour Rate',
    description: 'Works backwards from salary target, overheads, admin time, and billable days to your real charge-out rate.',
    demoTitle: 'Small contractor charge-out rate',
    demoSubtitle: 'Built for the gap between days worked and hours you can actually bill.',
    returns: ['Minimum hourly rate', 'Day rate', 'Overhead recovery'],
    inputs: [
      { key: 'salary', label: 'Owner salary', value: 420000, min: 180000, max: 900000, step: 10000, suffix: 'R/yr' },
      { key: 'overheads', label: 'Overheads', value: 168000, min: 40000, max: 420000, step: 5000, suffix: 'R/yr' },
      { key: 'workingDays', label: 'Working days', value: 220, min: 160, max: 245, step: 1, suffix: 'days' },
      { key: 'billableHours', label: 'Billable hours', value: 5.5, min: 3, max: 8, step: 0.5, suffix: '/day' },
      { key: 'profit', label: 'Profit buffer', value: 18, min: 0, max: 35, step: 1, suffix: '%' },
    ],
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
        <circle cx="10" cy="10" r="8" stroke={calculatorIconStroke} strokeWidth="1.4"/>
        <path d="M10 6v4l3 2" stroke={calculatorIconStroke} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    calculate: (values) => {
      const annualNeed = values.salary + values.overheads
      const billableHours = values.workingDays * values.billableHours
      const baseRate = annualNeed / billableHours
      const chargeRate = baseRate * (1 + values.profit / 100)
      const dayRate = chargeRate * values.billableHours
      const weeklyTarget = dayRate * 5

      return {
        headline: 'Minimum labour rate',
        total: `${currency(chargeRate)} / hour`,
        metrics: [
          { label: 'Day rate', value: currency(dayRate) },
          { label: 'Weekly target', value: currency(weeklyTarget) },
          { label: 'Billable hours', value: `${whole(billableHours)} / year` },
          { label: 'Annual recovery', value: currency(annualNeed) },
        ],
        assumptions: [
          `${whole(values.workingDays)} working days per year`,
          `${numberValue(values.billableHours)} billable hours per day`,
          `${whole(values.profit)}% profit buffer on top of salary and overheads`,
        ],
        handoff: [
          `Owner salary target: ${currency(values.salary)}`,
          `Annual overhead recovery: ${currency(values.overheads)}`,
          `Base break-even rate: ${currency(baseRate)} per hour`,
        ],
      }
    },
  },
  {
    id: 'job-profitability',
    trade: 'All trades',
    name: 'Job Profitability',
    description: 'Checks whether a job price still leaves profit after materials, labour, subcontractors, travel, and overhead allocation.',
    demoTitle: 'Bathroom refresh profitability check',
    demoSubtitle: 'A realistic job sanity check before sending the quote.',
    returns: ['Gross profit', 'Margin percent', 'Effective hourly return'],
    inputs: [
      { key: 'revenue', label: 'Quote value', value: 38500, min: 12000, max: 85000, step: 500, suffix: 'R' },
      { key: 'materials', label: 'Materials', value: 11800, min: 3000, max: 42000, step: 250, suffix: 'R' },
      { key: 'labourHours', label: 'Labour hours', value: 54, min: 12, max: 140, step: 1, suffix: 'hrs' },
      { key: 'labourRate', label: 'Labour cost', value: 165, min: 80, max: 320, step: 5, suffix: 'R/hr' },
      { key: 'overheads', label: 'Job overhead', value: 3600, min: 0, max: 12000, step: 100, suffix: 'R' },
    ],
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
        <path d="M3 15l4-5 4 3 4-6 2 2" stroke={calculatorIconStroke} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        <rect x="2" y="2" width="16" height="16" rx="2" stroke={calculatorIconStroke} strokeWidth="1.4"/>
      </svg>
    ),
    calculate: (values) => {
      const labourCost = values.labourHours * values.labourRate
      const totalCost = values.materials + labourCost + values.overheads
      const profit = values.revenue - totalCost
      const margin = values.revenue > 0 ? profit / values.revenue * 100 : 0
      const effectiveHourly = profit / values.labourHours

      return {
        headline: margin >= 25 ? 'Healthy quote margin' : 'Margin needs review',
        total: currency(profit),
        metrics: [
          { label: 'Margin', value: `${numberValue(margin)}%` },
          { label: 'Total cost', value: currency(totalCost) },
          { label: 'Labour cost', value: currency(labourCost) },
          { label: 'Profit per hour', value: currency(effectiveHourly) },
        ],
        assumptions: [
          `${whole(values.labourHours)} labour hours at ${currency(values.labourRate)} per hour`,
          `${currency(values.materials)} materials and ${currency(values.overheads)} overhead allocation`,
          `Target contractor margin should usually stay above 25%`,
        ],
        handoff: [
          `Quote value: ${currency(values.revenue)}`,
          `Direct costs: ${currency(values.materials + labourCost)}`,
          margin < 25 ? 'Review price, labour allowance, or material markup before sending.' : 'Ready to carry into a quote review.',
        ],
      }
    },
  },
]

function DemoControls({
  inputs,
  values,
  onChange,
}: {
  inputs: DemoInput[]
  values: Record<string, number>
  onChange: (key: string, value: number) => void
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {inputs.map((input) => (
        <label key={input.key} className="rounded-xl border border-slate-200 bg-white px-3 py-3">
          <span className="flex items-center justify-between gap-3 text-[12px] font-semibold text-slate-600">
            {input.label}
            <span className="text-slate-900">
              {numberValue(values[input.key] ?? input.value, input.step < 1 ? 1 : 0)}{input.suffix ? ` ${input.suffix}` : ''}
            </span>
          </span>
          <input
            type="range"
            min={input.min}
            max={input.max}
            step={input.step}
            value={values[input.key] ?? input.value}
            onChange={(event) => onChange(input.key, Number(event.target.value))}
            className="mt-3 h-3 w-full accent-[#c2571a] sm:h-2"
          />
        </label>
      ))}
    </div>
  )
}

export function MarketingCalculatorShowcase() {
  const [activeId, setActiveId] = useState<CalculatorId>('painting-interior')
  const activeCalculator = CALCULATORS.find((calc) => calc.id === activeId) ?? CALCULATORS[0]
  const [valuesByCalculator, setValuesByCalculator] = useState<Record<CalculatorId, Record<string, number>>>(() => (
    Object.fromEntries(CALCULATORS.map((calculator) => [calculator.id, getValues(calculator.inputs)])) as Record<CalculatorId, Record<string, number>>
  ))

  const activeValues = valuesByCalculator[activeCalculator.id]
  const result = useMemo(() => activeCalculator.calculate(activeValues), [activeCalculator, activeValues])

  const updateValue = (key: string, value: number) => {
    setValuesByCalculator((current) => ({
      ...current,
      [activeCalculator.id]: {
        ...current[activeCalculator.id],
        [key]: value,
      },
    }))
  }

  return (
    <section className="border-t border-slate-100 bg-[#f8fafc] py-11 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 max-w-2xl sm:mb-10">
          <p className="text-xs uppercase tracking-[0.18em] text-[#c2571a] font-semibold mb-3">Estimator Library</p>
          <h2 className="mb-3 text-[1.95rem] font-bold leading-[1.08] tracking-tight sm:mb-4 sm:text-[2.7rem] sm:leading-tight" style={{ color: '#0f172a' }}>
            Try the estimator before you sign up.
          </h2>
          <p className="text-[1rem] leading-relaxed text-slate-600 sm:text-[1.05rem] sm:text-slate-500">
            Each demo starts with a real contractor scenario, then lets you adjust the assumptions.
            BuilderBuddi shows the quantities, cost logic, and handoff notes a quote needs.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[0.92fr_1.08fr] lg:items-start">
          <div className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-1">
            {CALCULATORS.map((calc) => {
              const isActive = calc.id === activeCalculator.id
              return (
                <button
                  key={calc.id}
                  type="button"
                  onClick={() => setActiveId(calc.id)}
                  className={`group min-w-[78%] snap-start rounded-2xl border bg-white p-4 text-left transition-all duration-200 sm:min-w-0 sm:p-5 ${
                    isActive
                      ? 'border-[#c2571a] shadow-[0_12px_36px_rgba(194,87,26,0.12)]'
                      : 'border-slate-200 hover:border-[#c2571a]/25 hover:shadow-[0_8px_32px_rgba(194,87,26,0.07)]'
                  }`}
                >
                  <div className="mb-3 flex items-start justify-between sm:mb-4">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#c2571a]/8 shrink-0">
                      {calc.icon}
                    </div>
                    <span className="text-[11px] font-semibold text-slate-400 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-full">
                      {calc.trade}
                    </span>
                  </div>

                  <h3 className="text-[15px] font-bold text-slate-900 mb-1.5">{calc.name}</h3>
                  <p className="mb-3 text-[13px] leading-relaxed text-slate-600 sm:mb-3.5 sm:text-slate-500">{calc.description}</p>

                  <ul className="mb-4 space-y-1.5">
                    {calc.returns.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-[12px] text-slate-600">
                        <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#c2571a]/12 shrink-0">
                          <svg width="6" height="6" viewBox="0 0 6 6" fill="none" aria-hidden>
                            <path d="M1 3l1.5 1.5L5 1.5" stroke={calculatorIconStroke} strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>

                  <span className="flex items-center gap-1.5 text-[12px] font-semibold text-[#c2571a] group-hover:gap-2.5 transition-all duration-150">
                    Try this estimator
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                      <path d="M2 6h8M7 3l3 3-3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </button>
              )
            })}
          </div>

          <div className="rounded-[1.75rem] border border-slate-200 bg-white p-4 shadow-[0_20px_70px_rgba(15,23,42,0.08)] sm:p-6 lg:sticky lg:top-6">
            <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 sm:p-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#c2571a]">Live demo</p>
                  <h3 className="mt-2 text-2xl font-bold tracking-tight text-slate-900">{activeCalculator.demoTitle}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-500">{activeCalculator.demoSubtitle}</p>
                </div>
                <div className="rounded-2xl bg-[#1e3a5f] px-4 py-3 text-white sm:min-w-40">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/60">Estimated total</p>
                  <p className="mt-1 text-2xl font-bold">{result.total}</p>
                </div>
              </div>

              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {result.metrics.map((metric) => (
                  <div key={metric.label} className="rounded-xl border border-slate-200 bg-white px-3 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{metric.label}</p>
                    <p className="mt-1 text-lg font-bold text-slate-900">{metric.value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <p className="mb-3 text-sm font-bold text-slate-900">Adjust the assumptions</p>
              <DemoControls inputs={activeCalculator.inputs} values={activeValues} onChange={updateValue} />
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-sm font-bold text-slate-900">Assumptions shown</p>
                <ul className="mt-3 space-y-2 text-sm text-slate-600">
                  {result.assumptions.map((item) => (
                    <li key={item} className="flex gap-2">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#c2571a]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-sm font-bold text-slate-900">Quote handoff notes</p>
                <ul className="mt-3 space-y-2 text-sm text-slate-600">
                  {result.handoff.map((item) => (
                    <li key={item} className="flex gap-2">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#1e3a5f]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-3 rounded-2xl border border-[#c2571a]/20 bg-[#fff7ed] p-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-700">
                In the app, this can be saved to the Field Notebook and carried into a quote.
              </p>
              <Link
                href="/signup"
                className="inline-flex min-h-12 items-center justify-center rounded-xl bg-[#1e3a5f] px-5 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#16304f]"
              >
                Start using estimators
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:mt-10 sm:flex-row">
          <Link
            href="/signup"
            className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#1e3a5f] px-7 py-3.5 text-[14px] font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-[#16304f] sm:w-auto"
          >
            Access all 50+ estimators
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M2.5 7h9M8.5 4l3 3-3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
          <p className="text-[13px] text-slate-500 sm:text-slate-400">Free to start - no card required</p>
        </div>
      </div>
    </section>
  )
}
